import { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import ReactECharts from "echarts-for-react";
import { Lightbulb } from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIMARY = "#1E3A8A";
const CURRENT_YEAR = new Date().getFullYear();

const PAYMENT_STATUS = {
  paid: { color: "#22C55E", label: "Paid" },
  due: { color: "#3B82F6", label: "Due" },
  overdue: { color: "#F97316", label: "Overdue" },
} as const;

type PaymentStatus = keyof typeof PAYMENT_STATUS;

const SERIES_CONFIG = [
  {
    id: "CLIENT_PREMIUM",
    label: "Client Premium",
    color: PRIMARY,
    key: "clientPremium" as const,
    illustratedKey: "clientPremiumIllustrated" as const,
  },
  {
    id: "LENDER_PREMIUM",
    label: "Lender Premium",
    // eslint-disable-next-line
    color: PRIMARY + "cc",
    key: "lenderPremium" as const,
    illustratedKey: "lenderPremiumIllustrated" as const,
  },
  {
    id: "INTEREST_PAYMENT",
    label: "Interest Payment",
    // eslint-disable-next-line
    color: PRIMARY + "99",
    key: "clientInterestPayment" as const,
    illustratedKey: "clientInterestPaymentIllustrated" as const,
  },
  {
    id: "PLAN_FEE",
    label: "Plan Fee",
    // eslint-disable-next-line
    color: PRIMARY + "66",
    key: "clientPlanFee" as const,
    illustratedKey: "clientPlanFeeIllustrated" as const,
  },
  {
    id: "ORIGINATION_FEE",
    label: "Origination Fee",
    // eslint-disable-next-line
    color: PRIMARY + "33",
    key: "clientOriginationFees" as const,
    illustratedKey: null,
  },
] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FundingChartRow {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: number;
  clientPlanFee: number;
  clientInterestPayment: number;
  clientOriginationFees: number;
  lenderPremium: number;
  lenderOriginationFees: number;
  totalPolicyPremium: number;
  clientPremiumIllustrated: number;
  lenderPremiumIllustrated: number;
  clientInterestPaymentIllustrated: number;
  clientPlanFeeIllustrated: number;
}

interface TooltipState {
  row: FundingChartRow;
  x: number;
  y: number;
}

interface FundingChartProps {
  data: FundingChartRow[];
  activeFilters: Set<string>;
  isDark: boolean;
  selectedYear: number;
  loading?: boolean;
  showProjections: boolean;
  onShowProjectionsChange: (v: boolean) => void;
  isContact: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);

function getPaymentStatus(
  actual: number,
  _illustrated: number,
  calendarYear: number,
): PaymentStatus {
  if (actual > 0) return "paid";
  return calendarYear < CURRENT_YEAR ? "overdue" : "due";
}

// ── Status icon per payment state ─────────────────────────────────────────────

function StatusIcon({ status, size = 14 }: { status: PaymentStatus; size?: number }) {
  const { color } = PAYMENT_STATUS[status];

  if (status === "paid") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path
          d="M4 7L6 9.5L10 5"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "overdue") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path d="M7 4V7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="9.5" r="0.8" fill="white" />
      </svg>
    );
  }

  // due → blue hexagon with $
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7 0.5L13.2 3.75V10.25L7 13.5L0.8 10.25V3.75L7 0.5Z" fill={color} />
      <text
        x="7"
        y="9.5"
        textAnchor="middle"
        fontSize="5.5"
        fill="white"
        fontWeight="800"
        fontFamily="sans-serif"
      >
        $
      </text>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function FundingChart({
  data,
  activeFilters,
  isDark,
  selectedYear,
  loading,
  showProjections,
  onShowProjectionsChange,
  isContact,
}: FundingChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nextYear = selectedYear + 1;

  // ── ECharts option ───────────────────────────────────────────────────────────

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";

    const activeCfgs = SERIES_CONFIG.filter((s) => activeFilters.has(s.id));

    const series = [
      {
        name: "Funding",
        type: "bar",
        barMaxWidth: 56,
        barCategoryGap: "20%",
        data: data.map((row) => {
          const isProjection = row.year > selectedYear;

          if (isProjection && !showProjections) {
            return { value: 0, itemStyle: { color: "transparent" } };
          }

          const total = activeCfgs.reduce((sum, cfg) => {
            const val =
              isProjection && cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
            return sum + val;
          }, 0);

          if (isProjection) {
            return {
              value: total,
              itemStyle: {
                color: isDark ? "rgba(96,165,250,0.22)" : "rgba(147,197,253,0.45)",
                borderColor: isDark ? "rgba(96,165,250,0.7)" : "#3B82F6",
                borderType: "dashed",
                borderWidth: 1.5,
              },
            };
          }

          return {
            value: total,
            itemStyle: { color: PRIMARY },
          };
        }),
      },
    ];

    return {
      animation: true,
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: 56, right: 12, top: 12, bottom: 36 },
      xAxis: {
        type: "category",
        data: data.map((r) => `Age ${r.insuredAge}`),
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (v: number) => (v >= 1000 ? `$${v / 1000}K` : `$${v}`),
        },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
      },
      series,
    };
  }, [data, activeFilters, isDark, selectedYear, showProjections]);

  // ── Mouse event handlers ─────────────────────────────────────────────────────

  const cancelHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setTooltip(null), 160);
  };

  const onMouseOver = (params: any) => {
    cancelHide();
    const row = data[params.dataIndex];
    if (!row) return;
    const evt = params.event?.event as MouseEvent | undefined;
    if (!evt) return;
    setTooltip({ row, x: evt.clientX, y: evt.clientY });
  };

  // ── Tooltip state ────────────────────────────────────────────────────────────

  const tooltipRow = tooltip?.row ?? null;
  const isNextYear = tooltipRow?.year === nextYear;

  const hasUnpaid =
    isNextYear && tooltipRow
      ? SERIES_CONFIG.some(
          (s) =>
            activeFilters.has(s.id) &&
            s.illustratedKey !== null &&
            tooltipRow[s.key] === 0 &&
            tooltipRow[s.illustratedKey!] > 0,
        )
      : false;

  // ── Theme tokens ─────────────────────────────────────────────────────────────

  const borderColor = isDark ? "#3A3A50" : "#E5E7EB";
  const panelBg = isDark ? "#1E2030" : "#FFFFFF";
  const textMain = isDark ? "#F1F5F9" : "#1E293B";
  const textSub = isDark ? "#94A3B8" : "#64748B";

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Projections toggle ────────────────────────────────────────────── */}
      <div className="flex justify-end mb-1">
        <label
          className="flex items-center gap-1.5 cursor-pointer select-none"
          onClick={() => onShowProjectionsChange(!showProjections)}
        >
          <div
            className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-colors"
            style={{
              borderColor: showProjections ? PRIMARY : borderColor,
              backgroundColor: showProjections ? PRIMARY : "transparent",
            }}
          >
            {showProjections && (
              <svg width="8" height="6" viewBox="0 0 10 8" fill="none">
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <Lightbulb
            size={11}
            fill={showProjections ? `${PRIMARY}30` : "none"}
            stroke={showProjections ? PRIMARY : textSub}
          />
          <span
            className="text-[11px] font-medium"
            style={{ color: showProjections ? PRIMARY : textSub }}
          >
            Projections
          </span>
        </label>
      </div>

      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <ReactECharts
        option={option}
        style={{ height: 300, cursor: "default" }}
        notMerge
        lazyUpdate
        showLoading={loading}
        loadingOption={{
          text: "",
          color: PRIMARY,
          maskColor: isDark ? "rgba(30,32,48,0.8)" : "rgba(255,255,255,0.8)",
        }}
        onEvents={{ mouseover: onMouseOver, mouseout: scheduleHide }}
      />

      {/* ── Hover tooltip (portal) ────────────────────────────────────────── */}
      {tooltip &&
        tooltipRow &&
        createPortal(
          <div
            className="fixed rounded-xl border shadow-2xl"
            style={{
              left: Math.min(tooltip.x + 14, window.innerWidth - 260),
              top: Math.max(tooltip.y - 24, 8),
              background: panelBg,
              borderColor,
              minWidth: 230,
              zIndex: 99999,
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={() => setTooltip(null)}
          >
            {/* Items */}
            <div className="px-3 pt-2 pb-2.5 flex flex-col gap-2">
              {SERIES_CONFIG.map((cfg) => {
                if (!activeFilters.has(cfg.id)) return null;

                const actual = tooltipRow[cfg.key];
                const illustrated = cfg.illustratedKey ? tooltipRow[cfg.illustratedKey] : actual;

                let statusNode: React.ReactNode;
                let labelColor = textSub;

                if (isNextYear && cfg.illustratedKey) {
                  const status = getPaymentStatus(actual, illustrated, tooltipRow.planYear);
                  labelColor = PAYMENT_STATUS[status].color;
                  statusNode = <StatusIcon status={status} size={15} />;
                } else {
                  // Non-next-year: plain colored dot
                  statusNode = (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: cfg.color }}
                    />
                  );
                }

                const displayVal = isNextYear && cfg.illustratedKey ? illustrated : actual;

                return (
                  <div key={cfg.id} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {statusNode}
                      <span
                        className="text-[12px] font-medium truncate"
                        style={{ color: labelColor }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                    <span
                      className="text-[11px] font-semibold tabular-nums shrink-0"
                      style={{ color: textMain }}
                    >
                      {fmt(displayVal)}
                    </span>
                  </div>
                );
              })}

              {/* Total */}
              <div className="border-t pt-1.5 mt-0.5" style={{ borderColor }}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold" style={{ color: textSub }}>
                    Total Premium
                  </span>
                  <span className="text-[12px] font-bold" style={{ color: PRIMARY }}>
                    {fmt(tooltipRow.totalPolicyPremium)}
                  </span>
                </div>
              </div>

              {/* Pay Now — outline style, next year only, when there are unpaid items */}
              {isNextYear && hasUnpaid && (
                <button
                  type="button"
                  disabled={!isContact}
                  className="w-full mt-0.5 py-1.5 rounded-lg text-[12px] font-bold transition-all"
                  style={{
                    background: "transparent",
                    color: isContact ? "#3B82F6" : textSub,
                    border: `1.5px solid ${isContact ? "#3B82F6" : borderColor}`,
                    cursor: isContact ? "pointer" : "not-allowed",
                    opacity: isContact ? 1 : 0.5,
                  }}
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default FundingChart;
